import { isDevelopment, isSecureWindowContext } from "appConfiguration";
import { Box } from "@mui/material";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import type { TFunction } from "core/entities";
import { useLocale } from "core/hooks/useLocale";
import { usePermission } from "core/hooks/usePermission";
import { useTranslation } from "react-i18next";
import type { useUserMenu } from "./useUserMenu";

export function DevMenuItems({
  t,
  handleCloseUserMenu,
}: {
  t: TFunction;
  handleCloseUserMenu: ReturnType<typeof useUserMenu>["handleCloseUserMenu"];
}) {
  const canUseDevOptions = usePermission("Fixhub.DevOptions");
  const locale = useLocale();
  const { i18n } = useTranslation();
  if (!canUseDevOptions) {
    return null;
  }

  return (
    <>
      <Divider key="devOptionsDivider" textAlign="center">
        <Typography color="text.secondary" variant="overline">
          {t("devOptions")}
        </Typography>
      </Divider>
      <Box key="toggle-translation-keys" sx={{ mx: 1 }}>
        <FormControlLabel
          labelPlacement="end"
          data-testid="toggle-translation-keys"
          label={t("translationKeyToggleLabel")}
          control={
            <Switch
              checked={i18n.language === "cimode"}
              onChange={(_, checked) => {
                if (checked) {
                  i18n.changeLanguage("cimode");
                } else {
                  i18n.changeLanguage(locale);
                }
              }}
            />
          }
        />
      </Box>
      {isDevelopment && isSecureWindowContext && (
        <MenuItem
          key="copy-translations"
          onClick={async () => {
            await handleCloseUserMenu("copy-translations");
          }}
        >
          {t(`option.copy-translations`)}
        </MenuItem>
      )}
      {isDevelopment && (
        <MenuItem
          key="populate-localizations"
          onClick={async () => {
            await handleCloseUserMenu("populate-localizations");
          }}
        >
          {t(`option.populate-localizations`)}
        </MenuItem>
      )}
      <MenuItem
        key="localization-inspect-page"
        onClick={async () => {
          await handleCloseUserMenu("localization-inspect-page");
        }}
      >
        {t(`option.localization-inspect-page`)}
      </MenuItem>
      <MenuItem
        key="logo-debug"
        onClick={async () => {
          await handleCloseUserMenu("logo-debug");
        }}
      >
        {t(`option.logo-debug`, "Logo Debug Page")}
      </MenuItem>
      <MenuItem
        key="test-page"
        onClick={async () => {
          await handleCloseUserMenu("test-page");
        }}
      >
        {t(`option.test-page`)}
      </MenuItem>
      <MenuItem
        key="theme-inspect-page"
        onClick={async () => {
          await handleCloseUserMenu("theme-inspect-page");
        }}
      >
        {t(`option.theme-inspect-page`)}
      </MenuItem>
      <MenuItem
        key="customize-theme"
        onClick={async () => {
          await handleCloseUserMenu("customize-theme");
        }}
      >
        {t(`option.customize-theme`)}
      </MenuItem>
      <MenuItem
        key="app-config-info"
        onClick={async () => {
          await handleCloseUserMenu("app-config-info");
        }}
      >
        {t(`option.app-config-info`)}
      </MenuItem>
      <MenuItem
        key="clear-indexdb"
        onClick={async () => {
          await handleCloseUserMenu("clear-indexdb");
        }}
      >
        {t("option.clear-indexdb", "Clear IndexedDB")}
      </MenuItem>
      <Divider key="end-divider" sx={{ my: 2 }} />
    </>
  );
}
